/**
 * Players page — pure formatting helpers (currency, dates, avatar colour/initial).
 *
 * Page-local on purpose: these only serve the Players table + detail sheet, so
 * they live with the page rather than in the shared (claim-gated) `utils/`. The
 * avatar colour is a DETERMINISTIC hash of the email (or, for an account with none, its
 * id) into `AVATAR_PALETTE`, so the same address always renders the same colour — across re-sorts, reloads and the
 * eventual real API. Colours themselves stay in `theme/tokens.ts`.
 */
import type { TFunction } from 'i18next';

import type { Player } from '../../mock-data/types';
import { AVATAR_PALETTE, type AvatarColor } from '../../theme/tokens';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** "Jun 10, 2024" — the pretty date the "Registered At" column shows. */
export const prettyDate = (epochMs: number): string => {
  const d = new Date(epochMs);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

/** "Jun 10, 2024 · 14:30" — the fuller stamp the detail sheet shows. */
export const prettyDateTime = (epochMs: number): string => {
  const d = new Date(epochMs);
  return `${prettyDate(epochMs)} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * "$1,234" / "$1,234.56" — a USD amount. `cents` keeps two decimals (wallet
 * balances); otherwise whole dollars (lifetime wagered). Uses `en-US` grouping,
 * matching the inline money formatting elsewhere in the area (TopBar, StatValue).
 */
export const money = (amount: number, cents = false): string => {
  // Keep the minus OUTSIDE the `$` ("-$70,307", not "$-70,307") so negative GGR
  // (a player who is net up on the brand) reads cleanly.
  const digits = cents ? 2 : 0;
  const formatted = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  // A small loss that rounds to nothing is zero, not "-$0".
  const negative = amount < 0 && Number(Math.abs(amount).toFixed(digits)) !== 0;
  return `${negative ? '-' : ''}$${formatted}`;
};

// djb2-ish string hash → a stable non-negative integer. Small, fast and good
// enough to spread emails evenly across the 12-colour palette.
const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

/** The deterministic `{ bg, fg }` avatar colour for an email. */
export const avatarColor = (email: string): AvatarColor =>
  AVATAR_PALETTE[hashString(email.trim().toLowerCase()) % AVATAR_PALETTE.length];

/** What names a player on screen: the email, else the name, else the brand's player id (an account registered without
 *  an email). Takes `t` so the module stays hook-free. */
export const playerLabel = (player: Pick<Player, 'email' | 'name' | 'playerId'>, t: TFunction): string => {
  if (player.email !== '') return player.email;
  return player.name ?? t('Player #{{id}}', { id: player.playerId ?? '' });
};

/** The avatar glyph — the email's first letter, uppercased (falls back to "?"). */
export const avatarInitial = (email: string): string => {
  const first = email.trim().charAt(0);
  return first ? first.toUpperCase() : '?';
};
